import React, {useEffect, useRef, useState, forwardRef} from 'react'
import PropTypes from "prop-types";
import {
  getValue,
  errorMessage,
  isInvalid,
  onChange,
  getEffectDependencies, onBlur
} from "./Component";
import {trimSeconds} from "../../utils/DateUtils";
import {parseInt} from "lodash";

const TimePicker = props => {
  const [value, setValue] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [selectedMinute, setSelectedMinute] = useState(null);
  const [typedIndex, setTypedIndex] = useState(0);

  const hourList = ['', '', '00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '', '', ''];
  const minuteList = ['', '', '00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55', '', '', ''];
  const dropdownElementHeight = 32;

  const ref = useRef();
  const inputRef = useRef();
  const hourDropdownRef = useRef();
  const minuteDropdownRef = useRef();
  const onHourChange = newValue => {
    setSelectedHour(newValue);
  };
  const onMinuteChange = newValue => {
    setSelectedMinute(newValue);
  };

  useEffect(() => {
    setValue(getValue(props))
  }, [getValue(props)])

  useEffect(() => {
    const val = trimSeconds(getValue(props));
    if(val === ''){
      return;
    }
    if(selectedHour !== null && selectedMinute !== null){
      return;
    }
    const newHours = val.split(":")[0];
    const newMinutes = val.split(":")[1];
    setSelectedHour(newHours)
    setSelectedMinute(newMinutes)
    setValue(val);
  }, [...getEffectDependencies(props)]);

  useEffect(() => {
    if(selectedHour === null && selectedMinute === null){
      return;
    } else if(selectedHour !== null && selectedMinute === null) {
      setSelectedMinute("00");
    } else if(selectedHour === null && selectedMinute !== null) {
      setSelectedHour("00");
    }
    const newValue = (selectedHour + ":" + selectedMinute);
    setValue(newValue);

    if (selectedHour !== null && selectedMinute !== null && value !== newValue) {
      onChange(props, newValue)
      props.formField.clearValidation();
    }
  }, [selectedHour, selectedMinute])

  useEffect(() => {
    hourDropdownRef.current.addEventListener("mousedown", preventBlur, false);
    minuteDropdownRef.current.addEventListener("mousedown", preventBlur, false);
  },[])

  useEffect(() => {
      const hourDropdownElement = hourDropdownRef.current.querySelector('[value="'+ selectedHour +'"]');
      if(hourDropdownElement !== null){
        scrollToElement(hourDropdownElement);
      }

      const minuteDropdownElement = minuteDropdownRef.current.querySelector('[value="'+ selectedMinute +'"]');
      if(minuteDropdownElement !== null){
        scrollToElement(minuteDropdownElement);
      }
  },[typedIndex])

  useEffect(() => {
    selectNumber();
  }, [value])

  function selectNumber() {
    if(typedIndex < 2) {
      inputRef.current.setSelectionRange(0,2)
    } else if(typedIndex < 4) {
      inputRef.current.setSelectionRange(3,5);
    }
  }

  function scrollToElement(e, behaviour = "instant") {
    const topScroll = e.offsetTop - 2 * dropdownElementHeight;
    e.parentNode.scrollTo({
      top: topScroll,
      left: 0,
      behavior: behaviour
    });
  }
  function preventBlur(e){
    e.preventDefault();
  }

  function onInputFocus(e) {
    setTypedIndex(0);
    inputRef.current.setSelectionRange(0,2);
  }

  function onInputChange(e) {
    if(!e.nativeEvent){
      return
    }
    const enteredValue = e.nativeEvent.data;
    if(!/^[0-9]$/.test(enteredValue)){
      return;
    }

    if(typedIndex === 0){
      setSelectedHour("0" + enteredValue)
      setTypedIndex(1);
    } else if (typedIndex === 1){
      const newHours = selectedHour.substring(1) + enteredValue;
      if(parseInt(newHours) <= 23){
        setSelectedHour(newHours);
        setTypedIndex(2);
      } else {
        setSelectedMinute("0" + enteredValue);
        setTypedIndex(3);
      }
    } else if (typedIndex === 2){
      setSelectedMinute("0" + enteredValue);
      setTypedIndex(3);
    } else if (typedIndex === 3) {
      const newMinutes = selectedMinute.substring(1) + enteredValue;
      if(parseInt(newMinutes) <= 59){
        setSelectedMinute(newMinutes);
      }
      setTypedIndex(4);
    }
  }
  function clearInput(e) {
    preventBlur(e);
    setTypedIndex(0);
    setSelectedHour(null);
    setSelectedMinute(null);
    setValue(null)
    onChange(props, null);
  }

  return (
    <label
      className={"field has-float-label dropdown time-picker select loadable"}
      ref={ref}
    >
      <input
        id={props.id}
        name={props.id}
        ref={inputRef}
        onBlur={onBlur.bind(null, props)}
        onClick={selectNumber}
        value={value === null ? "--:--" : value}
        onChange={(e) => onInputChange(e)}
        onFocus={(e) => onInputFocus(e)}
        onBlur={props.formField.triggerValidation}
        type="text"
        className={isInvalid(props) ? "is-invalid input" : "input"}
        disabled={props.disabled}
      />
      <span>{props.label || ""}</span>

      <div className="error-icon fz fz-exclamation-triangle solid">
        <div className="has-tooltip danger" data-tooltip={errorMessage(props)}/>
      </div>
      <div className="dropdown-indicator right-decoration fz fz-clock"/>
      <div className="dropdown-indicator right-decoration fz fz-times clear-button"
           onMouseDown={(e) => clearInput(e)}
           onMouseUp={(e) => inputRef.current.blur()}
      />
      <div className="time-picker-container">
        <TimePickerDropdown elements={hourList}
                            ref={hourDropdownRef}
                            value={selectedHour}
                            onValueChange={onHourChange}
                            elementHeight={dropdownElementHeight}/>
        <div className="time-picker-separator dropdown">:</div>
        <TimePickerDropdown elements={minuteList}
                            ref={minuteDropdownRef}
                            value={selectedMinute}
                            onValueChange={onMinuteChange}
                            elementHeight={dropdownElementHeight}/>
      </div>
    </label>
  );
}

TimePicker.propTypes = {
  formField: PropTypes.object,
  isEditable: PropTypes.bool,
  label: PropTypes.string,
}
const TimePickerDropdown = forwardRef(function TimePickerDropdown(props, ref) {

  const [value, setValue] = useState(null);

  function scrollToElement(e, behaviour="smooth") {
    if(e === null){
      return;
    }
    const topScroll = e.offsetTop - 2 * props.elementHeight;
    e.parentNode.scrollTo({
      top: topScroll,
      left: 0,
      behavior: behaviour
    });
  }

  useEffect(() => {
    props.onValueChange(value);
  }, [value])

  function onMouseWheel(e){
    e.preventDefault();
    e.stopPropagation();

    let element = e.target;
    if(element.classList.contains("dropdown-element")){
      element = element.parentNode;
    }
    const isScrollingDown = e.deltaY > 0;
    element.scrollBy({
      top: isScrollingDown ? props.elementHeight : -props.elementHeight,
      left: 0});
    return false;
  }

  function onScroll(e) {
    const scrollOffset = e.target.scrollTop;
    const newElement = props.elements [Math.round(scrollOffset / props.elementHeight) + 1];
    setValue(newElement);
    if(!isNaN(props.value) && value === null) {
      scrollToElement(ref.current.querySelector('[value="'+ props.value +'"]'), "instant")
    }
  }

  useEffect(() => {
    ref.current.addEventListener('wheel', onMouseWheel, {passive: false});
  },[])

  return(
  <div
    className="dropdown"
    elements={props.elements}
    onClick={(e) => scrollToElement(e.target)}
    ref={ref}
    onScroll={(e) => onScroll(e)}
  >
    <div className="sticky-background"></div>
    {props.elements.map((element, index) =>
      <div
        className="dropdown-element"
        key={index}
        value={element}
      >{element}</div>
    )}
  </div>
  )
})


TimePickerDropdown.propTypes = {
  elements: PropTypes.array.isRequired,
  elementHeight: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
}


export default TimePicker
